import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useProfileHierarchy } from './use-profile-hierarchy';
import { preloadImage, isImageCached } from '@/lib/imageCache';

interface Profile {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  avatar_url: string | null;
  profile_type?: 'primary' | 'standard' | 'kids';
  created_at?: string;
  email_verified?: boolean;
  email_verified_at?: string;
  last_sign_in_at?: string;
  sign_in_count?: number;
  is_primary?: boolean;
  profile_settings?: {
    anonymous_mode?: boolean;
  };
}

interface UseStableAvatarReturn {
  currentProfile: Profile | null;
  avatarUrl: string | null;
  displayName: string;
  isLoading: boolean;
  isAvatarLoaded: boolean;
}

export function useStableAvatar(): UseStableAvatarReturn {
  const { session } = useAuth();
  const { activeProfile, isLoading: isProfileLoading } = useProfileHierarchy();
  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const avatarLoadingRef = useRef<string | null>(null);

  // Preload avatar when profile changes
  useEffect(() => {
    const avatarUrl = activeProfile?.avatar_url;
    
    if (!avatarUrl) {
      setIsAvatarLoaded(true);
      return;
    }

    // Prevent loading same image multiple times
    if (avatarLoadingRef.current === avatarUrl) {
      return;
    }
    avatarLoadingRef.current = avatarUrl;

    // Check cache first
    if (isImageCached(avatarUrl)) {
      setIsAvatarLoaded(true);
      return;
    }

    // Preload the image
    setIsAvatarLoaded(false);
    preloadImage(avatarUrl, 'high')
      .then(() => {
        if (avatarLoadingRef.current === avatarUrl) {
          setIsAvatarLoaded(true);
        }
      })
      .catch(() => {
        if (avatarLoadingRef.current === avatarUrl) {
          setIsAvatarLoaded(true); // Show fallback on error
        }
      });
  }, [activeProfile?.avatar_url]);

  // Memoize the current profile object for stable reference
  const currentProfile = useMemo((): Profile | null => {
    if (!activeProfile) return null;
    
    return {
      id: activeProfile.id,
      name: activeProfile.name,
      first_name: activeProfile.first_name ?? undefined,
      last_name: activeProfile.last_name ?? undefined,
      country: activeProfile.country ?? undefined,
      avatar_url: activeProfile.avatar_url,
      is_primary: activeProfile.is_primary,
      profile_type: activeProfile.profile_type,
      profile_settings: activeProfile.profile_settings,
      created_at: activeProfile.created_at,
    };
  }, [
    activeProfile?.id,
    activeProfile?.name,
    activeProfile?.first_name,
    activeProfile?.last_name,
    activeProfile?.country,
    activeProfile?.avatar_url,
    activeProfile?.is_primary,
    activeProfile?.profile_type,
    activeProfile?.profile_settings,
    activeProfile?.created_at,
  ]);

  const displayName = useMemo(() => {
    if (activeProfile?.name) return activeProfile.name;
    if (session?.user?.email) return session.user.email.split('@')[0];
    return 'User';
  }, [activeProfile?.name, session?.user?.email]);

  const avatarUrl = activeProfile?.avatar_url || null;

  return {
    currentProfile,
    avatarUrl,
    displayName,
    isLoading: isProfileLoading,
    isAvatarLoaded,
  };
}
